import { useMemo, useRef, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Hint, Spinner } from '@factorialco/deprecated-design-system'
import {
  PeopleChartEmployeeSidepanelDocument,
  PeopleChartStructureDocument,
  PeopleChartTeamsDocument,
} from 'generated/resources/graphql'
import keyBy from 'lodash/keyBy'
import { useResizeObserver } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import EmployeeSidepanel from 'modules/people_chart/components/EmployeeSidepanel'
import EmptyState from 'modules/people_chart/components/EmptyState'
import GroupChart from 'modules/people_chart/components/GroupChart'
import { useUpsertHierarchy } from 'modules/people_chart/components/GroupChart/useUpsertHierarchy'
import GroupSidepanel from 'modules/people_chart/components/GroupSidepanel'
import { PeopleChartContext } from 'modules/people_chart/hooks/usePeopleChartContext'
import hierarchyLib from 'modules/people_chart/lib/Hierarchy'
import hierarchy from 'modules/people_chart/lib/Hierarchy'
import { useTeamChartFeature } from 'modules/teams/hooks/useTeamChartFeature'

const PeopleChartPage = () => {
  const [chartEditing, setChartEditing] = useState(false)
  const [teamSidePanelEditing, setTeamSidepanelEditing] = useState(false)
  const [openedTeam, setOpenedTeam] = useState('0')
  const [isTeamSidepanelOpen, setIsTeamSidepanelOpen] = useState(false)
  const [isEmployeeSidepanelOpen, setIsEmployeeSidepanelOpen] = useState(false)
  const [hideEmptyState, setHideEmptyState] = useState(false)
  const { data: structureData, loading: loadingStructure } = useQuery(PeopleChartStructureDocument)
  const [fetchChartTeams, { data: teamsData, loading: loadingTeams, networkActivity }] =
    useLazyQuery(PeopleChartTeamsDocument)

  const { company } = useCurrentSession()
  const companyId = company.id
  const { teamChartFeatureEnabled } = useTeamChartFeature(companyId)

  const [fetchEmployeeDetail, { data: employeeDetail, loading: loadingEmployeeDetail }] =
    useLazyQuery(PeopleChartEmployeeSidepanelDocument)
  const [openHint, setOpenHint] = useState(false)

  const { upsert: upsertHierarchy } = useUpsertHierarchy()
  const selectedEmployeeDetail = employeeDetail?.employees?.employeesConnection.nodes[0]
  const hierarchyData = useUnsafeMemo(() => {
    return hierarchy.fromJSON(structureData?.peopleChart.structuresConnection.nodes[0]?.hierarchy)
  }, [structureData])

  const ref = useRef<HTMLDivElement>(null)
  const { width = 0, height = 0 } = useResizeObserver({
    ref,
    box: 'border-box',
  })

  const containerWidth = useMemo(() => {
    return width || window.innerWidth
  }, [width])

  const containerHeight = useMemo(() => {
    return height || window.innerHeight
  }, [height])

  const canEditPeopleChart = Boolean(structureData?.teams._policies.teamCreate.can)
  const hasErrors = hierarchyData === null
  const isEmpty = hierarchyLib.isEmpty(hierarchyData || [])
  const shouldSeeEmptyState = !hasErrors && isEmpty && !hideEmptyState
  const loading = loadingStructure || loadingTeams

  const structureId = String(structureData?.peopleChart.structuresConnection.nodes[0]?.id)

  const isLeaf = (groupId: string) => {
    const result: string[] = []
    hierarchyData?.some(function (group) {
      result.push(String(group.parentId))
    })

    return !result.includes(groupId)
  }

  const parentId = hierarchyLib.getParentId(String(openedTeam), hierarchyData || [])
  const activeTeamNeighbors = hierarchyLib.getNeighbors(String(openedTeam), hierarchyData || [])
  const handleTeamSelection = async (teamId: string, openSidepanel = true) => {
    setOpenedTeam(teamId)
    if (openSidepanel) {
      setIsTeamSidepanelOpen(true)
    }
  }

  const handleEmployeeSelection = async (employeeId: string) => {
    await fetchEmployeeDetail({
      variables: { ids: [employeeId] },
    })
    setIsEmployeeSidepanelOpen(true)
  }

  const upsert = async (hierarchyJson: string) => {
    const result = await upsertHierarchy({
      variables: {
        hierarchy: hierarchyJson,
        companyId: companyId,
        id: structureId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: PeopleChartStructureDocument,
        },
      ],
    })

    const handled = handleResult(result, () => undefined)

    if (!handled.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('people_chart.employees_selector.error_modifying_chart'),
      })
    }
  }

  useEffect(() => {
    const loadTeams = async () => {
      await fetchChartTeams()
    }
    if (!isEmpty) {
      loadTeams()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty])

  const removeTeamFromChart = (teamId: string, parent: string) => {
    setOpenedTeam('0')
    const result = hierarchyLib.removeNode(
      { parentId: parent, id: teamId, type: 'team' },
      hierarchyData || []
    )
    return upsert(hierarchyLib.toJSON(result))
  }

  const isChartEditing = (hierarchyData || []).length > 1 ? chartEditing : canEditPeopleChart

  const handleCloseTeamSidepanel = () => {
    setIsTeamSidepanelOpen(false)
    setOpenedTeam('0')
  }

  const handleCloseEmployeeSidepanel = () => {
    setIsEmployeeSidepanelOpen(false)
  }

  const teams = teamsData?.teams.teams || []
  const indexedTeams = keyBy(teams, 'id')
  const activeTeamMembers = indexedTeams[openedTeam]?.allMembers.nodes || []
  const activeEmployeeIndex = activeTeamMembers.findIndex(
    (member) => member.employee.id === selectedEmployeeDetail?.id
  )
  const previousEmployeeId = activeTeamMembers[activeEmployeeIndex - 1]?.employee.id
  const nextEmployeeId = activeTeamMembers[activeEmployeeIndex + 1]?.employee.id

  const totalTeams = teams.length
  const chartTeams = Array.from(hierarchyLib.identifiersAsNumber('team', hierarchyData || []))
  const chartTeamsSize = chartTeams.length

  useEffect(() => {
    if (!loading) {
      track(trackingEvents.teamChart.goToTeamChart, {
        teams_added: chartTeamsSize,
        total_teams: totalTeams,
      })
    }
  }, [totalTeams, chartTeamsSize, loading])

  const handleCloseHint = () => {
    setOpenHint(false)
  }

  const handleStart = async () => {
    await fetchChartTeams()
    setHideEmptyState(true)
  }

  return (
    <PeopleChartContext.Provider value={{ hierarchyTeamIds: chartTeams }}>
      <ResponsivePage.Body
        variant="fullscreen"
        loading={false}
        error={!teamChartFeatureEnabled}
        showHeader={false}
      >
        <Box width="full" height="full" gap="s24" ref={ref}>
          {(loadingStructure || networkActivity) && (
            <Box alignItems="center" justifyContent="center" height="full">
              <Spinner />
            </Box>
          )}
          {!loadingStructure && shouldSeeEmptyState && (
            <EmptyState canEdit={canEditPeopleChart} onStart={handleStart} />
          )}

          {/* TODO: if hierarchy data is null there is a problem processing show proper page with error */}
          {!loading && !hasErrors && !shouldSeeEmptyState && !networkActivity && (
            // This div with width and height is needed
            // if not the page grows vertically without stopping
            // needs to figure out why
            <div
              style={{
                width: containerWidth,
                height: containerHeight,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <GroupChart
                size={[containerWidth, containerHeight]}
                teams={teams}
                hierarchy={hierarchyData || []}
                canEdit={canEditPeopleChart}
                editing={isChartEditing}
                activeTeamId={String(openedTeam)}
                upsert={upsert}
                onChartEditing={setChartEditing}
                onTeamSelection={handleTeamSelection}
                onEmployeeSelection={handleEmployeeSelection}
                setOpenTeam={setOpenedTeam}
                setSidePanelEditing={setTeamSidepanelEditing}
              />
              {openedTeam !== '0' && isTeamSidepanelOpen && (
                <GroupSidepanel
                  setSidePanelEditing={setTeamSidepanelEditing}
                  isChartEditing={isChartEditing}
                  canEdit={canEditPeopleChart}
                  editing={teamSidePanelEditing}
                  onClose={handleCloseTeamSidepanel}
                  canRemove={isLeaf(openedTeam)}
                  onRemove={removeTeamFromChart}
                  parentId={parentId}
                  previousTeamId={
                    activeTeamNeighbors.left ? String(activeTeamNeighbors.left) : undefined
                  }
                  nextTeamId={
                    activeTeamNeighbors.right ? String(activeTeamNeighbors.right) : undefined
                  }
                  onTeamSelection={handleTeamSelection}
                  setOpenHint={setOpenHint}
                  onEmployeeSelection={handleEmployeeSelection}
                  teamId={openedTeam}
                />
              )}
              {openHint && (
                <Box style={{ position: 'absolute', top: '30%', left: '20%' }}>
                  <Hint.Popover.Provider steps={1} onClose={handleCloseHint}>
                    <Hint.Popover
                      step={1}
                      title={i18n.t('people_chart.hint.title')}
                      description={i18n.t('people_chart.hint.description')}
                      actionLabel={i18n.t('people_chart.hint.button')}
                      position="top left"
                    />
                  </Hint.Popover.Provider>
                </Box>
              )}
              {isEmployeeSidepanelOpen && selectedEmployeeDetail && (
                <EmployeeSidepanel
                  basicInfo={selectedEmployeeDetail}
                  onClose={handleCloseEmployeeSidepanel}
                  previousEmployeeId={previousEmployeeId}
                  nextEmployeeId={nextEmployeeId}
                  onEmployeeSelection={handleEmployeeSelection}
                  onTeamSelection={handleTeamSelection}
                  loadingData={loadingEmployeeDetail}
                />
              )}
            </div>
          )}
        </Box>
        <Outlet />
      </ResponsivePage.Body>
    </PeopleChartContext.Provider>
  )
}

export default PeopleChartPage
